import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import Chip from 'components/@extended/Chip.tsx';
import MatterIndicators, { hasMatterAccess } from 'components/matters/MatterIndicators';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import HoursDisplay from 'components/util/HoursDisplay';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useMemo } from 'react';
import { ClassifierData } from 'types/classifiers';
import { MatterStateType } from 'types/matters.ts';
import { TimecardListItemData, TimecardStatusType } from 'types/timecards';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useEmployeeNameFormatter } from 'utils/nameOrder';
import LabelledData from './LabelledData';
import TimecardActionButtons from './TimecardActionButtons';
import { TimecardEmployeeLabel } from './labels/TimecardEmployeeLabel';
import { TimecardStatusLabel } from './labels/TimecardStatusLabel';
import { ClosedCaseDraftMarker } from './list/ClosedCaseDraftMarker';

/** The view/edit links: the link colour at rest, underlined on hover. */
const TIMECARD_LINK_CLASS = 'text-link w-fit no-underline hover:underline';

type Props = {
    timecard: TimecardListItemData;
    classifier: ClassifierData;
    classifiers: Record<string, ClassifierData>;
    compact?: boolean;
};

const TimecardListItem = ({ timecard, classifier, classifiers, compact }: Props): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    const formatName = useEmployeeNameFormatter();
    const expenseList = timecard?.timecardStats?.expenseList || [];
    const { matter } = timecard;
    const indicators = <MatterIndicators isConfidential={matter.isConfidential} hasAccess={hasMatterAccess(matter)} />;
    const nonBillableChip = (
        <Chip className="mt-1 cursor-pointer" size="small" label={<MoneyOffRoundedIcon />} type={'partyType'} />
    );
    const isCaseClosed =
        matter.matterStateTypeCode === MatterStateType.COMPLETED ||
        matter.matterStateTypeCode === MatterStateType.OFFER_LOST ||
        matter.matterStateTypeCode === MatterStateType.OFFER_WON;
    const isDraftTimecard = timecard.timecardStatusTypeCode === TimecardStatusType.DRAFT;

    const timecardLink = useMemo(() => {
        if (timecard.isEditable) {
            return (
                <span className="inline-flex flex-row gap-1">
                    <LinkWithOrigin className={TIMECARD_LINK_CLASS} to={`/timecards/${timecard.id}`}>
                        {t('timecards.view-timecard')}
                    </LinkWithOrigin>
                    {' | '}
                    <LinkWithOrigin className={TIMECARD_LINK_CLASS} to={`/timecards/${timecard.id}/edit`}>
                        {t('timecards.edit-timecard')}
                    </LinkWithOrigin>
                </span>
            );
        } else {
            return (
                <LinkWithOrigin className={TIMECARD_LINK_CLASS} to={`/timecards/${timecard.id}`}>
                    {t('timecards.view-timecard')}
                </LinkWithOrigin>
            );
        }
    }, [timecard.id, timecard.isEditable]);

    return (
        <div className="flex w-full p-4">
            <ClosedCaseDraftMarker timecard={timecard} className="mr-2 shrink-0" />
            <div className="flex min-w-0 flex-1 flex-wrap">
                <div className={compact ? 'w-1/2 md:w-2/3' : 'w-5/12'}>
                    <div className="flex flex-col">
                        {matter.clientParty && (
                            <h5 className="text-h5 text-fg font-medium">
                                <PartyName
                                    party={matter.clientParty}
                                    isLink
                                    useLinkWithOrigin
                                    linkTarget="_blank"
                                    variant="readonly-other"
                                    showPartyNumber
                                />
                            </h5>
                        )}
                        {isNewDesign ? (
                            <>
                                <h5 className="text-h5">
                                    <RecordLink
                                        to={`/matter/${matter.id}`}
                                        label={`${matter.caseNumber} ${matter.name}`}
                                        target="_blank"
                                        withOrigin
                                    />
                                </h5>
                                {/* The case's chips on a line of their own under it, the title leading. */}
                                {compact ? (
                                    <div className="flex items-center gap-1">
                                        {indicators}
                                        {timecard.isNonBillable ? nonBillableChip : null}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <LinkWithOrigin
                                className="text-fg hover:text-link w-fit no-underline hover:underline"
                                to={`/matter/${matter.id}`}
                                target="_blank"
                            >
                                <div className="new:flex-wrap flex items-center gap-1">
                                    {indicators}
                                    {compact && timecard.isNonBillable ? nonBillableChip : null}
                                    <h5 className="text-h5 new:order-first new:basis-full">
                                        <span className="font-bold">{matter.caseNumber + ' '}</span>
                                        <span className="font-medium">{matter.name}</span>
                                    </h5>
                                </div>
                            </LinkWithOrigin>
                        )}
                        {!compact && (
                            <div className="flex gap-1">
                                {isNewDesign ? indicators : null}
                                {timecard.isNonBillable ? nonBillableChip : null}
                                <TimecardStatusLabel
                                    classifier={classifier}
                                    statusCode={timecard.timecardStatusTypeCode}
                                />
                                <TimecardEmployeeLabel name={formatName(timecard.employee)} />
                            </div>
                        )}
                        {!compact && (
                            <div className="mt-4 flex flex-row flex-wrap *:mr-2">
                                {timecard.matterAssignment?.name !== null &&
                                    timecard?.matterAssignment?.name !== undefined && (
                                        <LabelledData
                                            label="timecards.list.matter-assignment"
                                            className="font-semibold"
                                        >
                                            {timecard?.matterAssignment?.name}
                                        </LabelledData>
                                    )}
                                {timecard?.timecardActivityTypeCode !== null &&
                                    timecard?.timecardActivityTypeCode !== undefined && (
                                        <LabelledData label="timecards.list.activity-type" className="font-semibold">
                                            {getClassifierText(
                                                classifiers['TIMECARD_ACTIVITY_TYPE'],
                                                timecard?.timecardActivityTypeCode
                                            )}
                                        </LabelledData>
                                    )}
                                <LabelledData label="timecards.list.total-time" className="font-semibold">
                                    <HoursDisplay value={timecard?.timecardStats?.totalTimeInHours} />
                                </LabelledData>
                                <LabelledData label="timecards.list.billable-time" className="font-semibold">
                                    <HoursDisplay value={timecard?.timecardStats?.billedTimeInHours} />
                                </LabelledData>
                                <LabelledData label="timecards.list.expenses" className="font-semibold">
                                    {expenseList.length ? (
                                        expenseList.map((expenseMap, index) => {
                                            const optionalComma = index < expenseList.length - 1 ? `,\u00A0` : null;

                                            return Object.keys(expenseMap).map((currencyKey) => (
                                                <span key={currencyKey}>
                                                    <MoneyDisplay
                                                        amount={expenseMap[currencyKey]}
                                                        currency={currencyKey}
                                                    />
                                                    {optionalComma}
                                                </span>
                                            ));
                                        })
                                    ) : (
                                        <span className="font-semibold">{` -`}</span>
                                    )}
                                </LabelledData>
                                <LabelledData label="timecards.list.total-amount">
                                    <span className="font-semibold">
                                        <MoneyDisplay
                                            amount={timecard?.timecardStats?.amountInOriginalCurrency || 0}
                                            currency={timecard.timecardStats?.originalCurrencyTypeCode}
                                        />
                                    </span>
                                    <span className="ml-[0.5ch]">
                                        (
                                        <MoneyDisplay
                                            amount={timecard?.currencyDetails?.totalAmountInBaseCurrency || 0}
                                            currency={timecard?.currencyDetails?.baseCurrency}
                                        />
                                        )
                                    </span>
                                </LabelledData>
                            </div>
                        )}
                        {compact && (
                            <div className="flex flex-col gap-2 pt-2">
                                <p className="text-body1">{timecard.description}</p>
                                {isCaseClosed && isDraftTimecard ? null : timecardLink}
                            </div>
                        )}
                    </div>
                </div>
                {!compact && (
                    <div className="w-5/12">
                        <div className="flex flex-col gap-4">
                            <p className="text-body1">{timecard.description}</p>
                            {isCaseClosed && isDraftTimecard ? null : timecardLink}
                        </div>
                    </div>
                )}
                <div className={cn('self-center', compact ? 'w-1/2 md:w-1/3' : 'w-1/6')}>
                    <div className="flex items-center justify-end gap-2">
                        {compact && (
                            <span className="text-caption pr-2">
                                <HoursDisplay value={timecard?.timecardStats?.totalTimeInHours || 0} />
                            </span>
                        )}
                        <TimecardActionButtons timecard={timecard} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TimecardListItem;
